import { Group, Skeleton, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { AiSummaryBasePanel } from 'app/Private/Clients/Detail/InfoTab/components/AiSummaryPanel/AiSummaryBasePanel';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { first, sample } from 'remeda';
import { animatedGradientText, skeletonLoadingText } from './AiSummaryPanel.styles';

type AiSummaryGenerationInProgressProps = {
  debtorName: string;
};

export const AiSummaryGenerationInProgress = ({
  debtorName,
}: AiSummaryGenerationInProgressProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const loadingMessages = useLoadingMessages(debtorName);

  const [loadingText, setLoadingText] = useState(first(sample(loadingMessages, 1)));

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingText(first(sample(loadingMessages, 1)));
    }, 4000);

    return () => clearInterval(interval);
  }, [loadingMessages]);

  return (
    <AiSummaryBasePanel
      title={
        <Group gap={6}>
          <IconSparkles color={theme.colors.purple[6]} size={18} stroke={1.5} />
          <Text className={animatedGradientText}>{t(i18nKeys.AI_SUMMARY.BILLING_PANEL.TITLE)}</Text>
        </Group>
      }
    >
      <Stack>
        <AnimatePresence mode="wait">
          <motion.span
            key={loadingText}
            className={skeletonLoadingText}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {loadingText}
          </motion.span>
        </AnimatePresence>
        <Skeleton w="100%" />
      </Stack>
    </AiSummaryBasePanel>
  );
};

const useLoadingMessages = (debtorName: string) => {
  const { t } = useTranslation();
  return [
    t(i18nKeys.AI_SUMMARY.LOADING_MESSAGES.PREPARING, { clientName: debtorName }),
    t(i18nKeys.AI_SUMMARY.LOADING_MESSAGES.REVIEWING_TRANSACTIONS),
    t(i18nKeys.AI_SUMMARY.LOADING_MESSAGES.ANALYZING_BALANCES),
    t(i18nKeys.AI_SUMMARY.LOADING_MESSAGES.SUMMARIZING_METRICS),
    t(i18nKeys.AI_SUMMARY.LOADING_MESSAGES.COMPILING_OVERVIEW),
    t(i18nKeys.AI_SUMMARY.LOADING_MESSAGES.FINALIZING),
  ];
};
